import { ApiService } from '../apiService'

/**
 * One-off maintenance jobs.
 *
 * Every one of these changes production data in bulk, so every one takes a `dryRun` that defaults
 * to true on the server as well as here. The pattern the UI follows is: preview, read the numbers,
 * then run — because "how many rows would this touch" is a question worth answering before the
 * answer stops being reversible.
 */

export interface RotateDerivedShareCodesResult {
  dryRun: boolean
  usersScanned: number
  /** How many hold a code that was derived from their email or username. */
  derived: number
  rotated: number
  skipped: number
  notes: string[]
}

export interface ResetDemoPasswordsResult {
  dryRun: boolean
  matched: number
  reset: number
  failures: string[]
  accounts: string[]
}

export const adminMaintenanceApi = {
  /**
   * Retire share codes that were built from their owner's email address.
   *
   * Only codes that actually leak are touched — the old generator is re-run and compared, so a
   * code that is already random is left alone. `excludeEmail` protects one account; the server
   * defaults it to the admin.
   */
  rotateDerivedShareCodes: (dryRun: boolean, excludeEmail?: string) =>
    ApiService.post<RotateDerivedShareCodesResult>(
      `/admin/maintenance/rotate-derived-share-codes?dryRun=${dryRun}` +
        (excludeEmail ? `&excludeEmail=${encodeURIComponent(excludeEmail)}` : '')
    ),

  /**
   * Set every User*@hadv.quest demo account to a known password. The password goes in the body,
   * never the URL, which ends up in access logs.
   */
  resetDemoPasswords: (dryRun: boolean, password?: string) =>
    ApiService.post<ResetDemoPasswordsResult>(
      `/admin/maintenance/reset-demo-passwords?dryRun=${dryRun}`,
      password ? { password } : {}
    ),
}
